import { getMediaPlayedWithProducts } from '../assets/libs/broox/brooxCloudApi';
import { Filter, FilterService } from './Filter';
import { ISaleService } from '../services/ISaleService';
import { AccountParams } from './AccountParams';
import { toLocalDate } from './DateConversion';
import { resolveQueryWindow } from './QueryWindow';

export interface ProductSales {
  name: string,
  code: number,
  media: string,
  totalPlays: number,
  units: number,
  totalSales: number
}

export interface FamilySales {
  name: string,
  media: string,
  totalPlays: number,
  units: number,
  totalSales: number
}

export interface BrandSales {
  name: string,
  media: string,
  totalPlays: number,
  units: number,
  totalSales: number
}

interface Sales {
  productSales: ProductSales[],
  familySales: FamilySales[],
  brandSales: BrandSales[],
  productSalesUnits: number,
  productSalesAmount: number,
  familySalesUnits: number,
  familySalesAmount: number,
  brandSalesUnits: number,
  brandSalesAmount: number
}

// Cada code viene prefijado con su tipo, p.ej. 'P:12545', 'F:familia', 'B:marca'.
const parseMediaCode = (raw: string): { type: 'P' | 'F' | 'B' | null, value: string } => {
  const separatorIndex = raw.indexOf(':');
  if(separatorIndex === -1) {
    return { type: null, value: raw };
  }
  const type = raw.substring(0, separatorIndex).trim().toUpperCase();
  const value = raw.substring(separatorIndex + 1).trim();
  return (type === 'P' || type === 'F' || type === 'B')
    ? { type, value }
    : { type: null, value: raw };
}

const getProductSales = async (filter: Filter, saleService: ISaleService, params: AccountParams): Promise<Sales> => {
  filter = FilterService.parse(filter);
  // Ventana semiabierta (CX-638 §05/Fase 6): '<' contra un toMs exclusivo,
  // nunca '<=' — así compone con la ventana del día siguiente sin solape.
  const range = { start: toLocalDate(filter.from || new Date()), end: toLocalDate(filter.to || new Date()) };
  const { fromMs, toMs } = resolveQueryWindow(range, filter.cutoff ?? null);
  const conditions: { field: string, operator: string, value: any }[] = [{ field: 'timestamp', operator: '>=', value: fromMs }, { field: 'timestamp', operator: '<', value: toMs }];
  const locationCondition = FilterService.getLocationCondition(filter);
  locationCondition && conditions.push(locationCondition);
  const aggregatedByProduct = new Map<string, ProductSales>();
  const aggregatedByFamily = new Map<string, FamilySales>();
  const aggregatedByBrand = new Map<string, BrandSales>();
  const mediaByProduct = new Map<string, { media: string, totalPlays: number }>();
  const mediaByFamily = new Map<string, { media: string, totalPlays: number }>();
  const mediaByBrand = new Map<string, { media: string, totalPlays: number }>();
  const mediaPlayed = await getMediaPlayedWithProducts(filter, params.host, params.token || params.id, params.apiKey);
  for(const media of mediaPlayed) {
    for(const code of media.codes) {
      const { type, value } = parseMediaCode(code);
      const byType = type === 'P' ? mediaByProduct : type === 'F' ? mediaByFamily : type === 'B' ? mediaByBrand : null;
      if(!byType) {
        continue;
      }
      let existing = byType.get(value);
      if(!existing) {
        existing = { media: media.mediaName, totalPlays: 0 };
      }
      existing.totalPlays += media.played;
      byType.set(value, existing);
    }
  }
  const codes = Array.from(mediaByProduct.keys().map(c => Number(c)));
  let sales = codes.length
    ? await saleService.andWhere([ ...conditions, { field: 'code', operator: 'in', value: codes }])
    : [];
  for(const sale of sales) {
    const byProduct = aggregatedByProduct.get(sale.code.toString());
    if(byProduct) {
      byProduct.units += sale.units || 0;
      byProduct.totalSales += sale.amount || 0;
      continue;
    }
    let relatedMedia = mediaByProduct.get(sale.code.toString());
    relatedMedia && aggregatedByProduct.set(sale.code.toString(), {
      name: sale.name,
      code: sale.code,
      media: relatedMedia?.media || 'N/A',
      totalPlays: relatedMedia?.totalPlays || 0,
      units: sale.units || 0,
      totalSales: sale.amount || 0
    });
  }

  const families = Array.from(mediaByFamily.keys());
  let salesByFamily = families.length
    ? await saleService.byFamilyAndWhere([ ...conditions, { field: 'name', operator: 'in', value: families }])
    : [];
  for(const sale of salesByFamily) {
    const byFamily = aggregatedByFamily.get(sale.name);
    if(byFamily) {
      byFamily.units += sale.units || 0;
      byFamily.totalSales += sale.amount || 0;
      continue;
    }
    const relatedMedia = mediaByFamily.get(sale.name);
    relatedMedia && aggregatedByFamily.set(sale.name, {
      name: sale.name,
      media: relatedMedia?.media || 'N/A',
      totalPlays: relatedMedia?.totalPlays || 0,
      units: sale.units || 0,
      totalSales: sale.amount || 0
    });
  }

  const brands = Array.from(mediaByBrand.keys());
  let salesByBrand = brands.length
    ? await saleService.byBrandAndWhere([ ...conditions, { field: 'name', operator: 'in', value: brands }])
    : [];
  for(const sale of salesByBrand) {
    const byBrand = aggregatedByBrand.get(sale.name);
    if(byBrand) {
      byBrand.units += sale.units || 0;
      byBrand.totalSales += sale.amount || 0;
      continue;
    }
    const relatedMedia = mediaByBrand.get(sale.name);
    relatedMedia && aggregatedByBrand.set(sale.name, {
      name: sale.name,
      media: relatedMedia?.media || 'N/A',
      totalPlays: relatedMedia?.totalPlays || 0,
      units: sale.units || 0,
      totalSales: sale.amount || 0
    });
  }
  const productSales = Array.from(aggregatedByProduct.values()).sort((a, b) => b.totalSales - a.totalSales);
  const familySales = Array.from(aggregatedByFamily.values()).sort((a, b) => b.totalSales - a.totalSales);
  const brandSales = Array.from(aggregatedByBrand.values()).sort((a, b) => b.totalSales - a.totalSales);
  let productSalesAmount = 0;
  let productSalesUnits = 0;
  let familySalesAmount = 0;
  let familySalesUnits = 0;
  let brandSalesAmount = 0;
  let brandSalesUnits = 0;
  for(const productSale of productSales) {
    productSalesAmount += productSale.totalSales;
    productSalesUnits += productSale.units;
  }
  for(const familySale of familySales) {
    familySalesAmount += familySale.totalSales;
    familySalesUnits += familySale.units;
  }
  for(const brandSale of brandSales) {
    brandSalesAmount += brandSale.totalSales;
    brandSalesUnits += brandSale.units;
  }
  return {
    productSales: productSales,
    productSalesAmount: productSalesAmount,
    productSalesUnits: productSalesUnits,
    familySales: familySales,
    familySalesAmount: familySalesAmount,
    familySalesUnits: familySalesUnits,
    brandSales: brandSales,
    brandSalesAmount: brandSalesAmount,
    brandSalesUnits: brandSalesUnits
  };
}

const getPreviousProductSales = async (filter: Filter, saleService: ISaleService, params: AccountParams): Promise<Sales> => {
  filter = FilterService.parse(filter);
  filter = FilterService.previous(filter);
  return await getProductSales(filter, saleService, params);
}

export { Sales, getProductSales, getPreviousProductSales };
